import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { apiFetch } from "@/modules/shared/lib/api-client";
import { usePeriod } from "@/modules/shared/dashboard/PeriodContext";

type ListResponse<T> = { items: T[]; totalCount: number };

type Props<T> = {
  title: string;
  endpoint: string;
  icon: LucideIcon;
  seeAllHref: string;
  renderItem: (item: T) => React.ReactNode;
  tone?: "ink" | "blue" | "amber" | "red" | "green";
};

export function ListWidget<T>({ title, endpoint, icon: Icon, seeAllHref, renderItem, tone = "ink" }: Props<T>) {
  const period = usePeriod();
  const url = `${endpoint}?period=${period}`;

  const { data, isLoading } = useQuery<ListResponse<T>>({
    queryKey: ["widget", endpoint, period],
    queryFn: () => apiFetch<ListResponse<T>>(url),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  return (
    <div className="ol-card flat col-span-2 flex flex-col gap-3">
      <div className="ol-card-head">
        <div className={`ol-card-ic ${tone}`}><Icon /></div>
        <div className="ol-card-title">
          <div className="ol-card-name"><span className="n">{title}</span></div>
        </div>
        {data && data.totalCount > 0 && (
          <span className="ol-kpi-l" style={{ whiteSpace: "nowrap" }}>{data.totalCount} no total</span>
        )}
      </div>

      {isLoading && (
        <div className="text-sm text-[var(--ink-3)] py-2">Carregando…</div>
      )}
      {data && data.items.length === 0 && (
        <div className="text-sm text-[var(--ink-3)] italic py-4 text-center">
          Tudo em ordem aqui ✓
        </div>
      )}
      {data && data.items.length > 0 && (
        <ul className="flex flex-col divide-y divide-[var(--line-soft)]">
          {data.items.map((it, i) => (
            <li key={i} className="px-1 py-2 text-sm transition-colors hover:bg-[var(--bone)] rounded">
              {renderItem(it)}
            </li>
          ))}
        </ul>
      )}

      <Link to={seeAllHref} className="ol-tag ol-tag-ghost mt-auto" style={{ alignSelf: "flex-start" }}>
        Ver todos <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}
